/**
 * Model class for equipment entry validation
 */
import { required, requiredIf } from 'vuelidate/lib/validators'
import * as _ from 'lodash'
import RetrofitManufacturer from './RetrofitManufacturer'
import RetrofitDevice from './RetrofitDevice'
import Api from '@/providers/Api'

const onRoadTypeOptions = [
    { value: 'DUMP_TRUCK', text: 'Dump Truck' },
    { value: 'CONCRETE_MIXER', text: 'Concrete Mixer' },
]
const nonRoadTypeOptions = [
    { value: 'AERIAL_LIFT', text: 'Aerial Lift' },
    { value: 'BACKHOE_LOADER', text: 'Backhoe Loader' },
    { value: 'BORE_OR_DRILL_RIG', text: 'Bore or Drill Rig' },
    { value: 'CEMENT_OR_MORTAR_MIXER', text: 'Cement or Mortar Mixer' },
    { value: 'CRANE', text: 'Crane' },
    { value: 'CRAWLER_DOZER_OR_LOADER', text: 'Crawler Dozer or Loader' },
    { value: 'CRUSHING_OR_PROCESSING_EQUIPMENT', text: 'Crushing or Processing Equipment' },
    { value: 'EXCAVATOR', text: 'Excavator' },
    { value: 'MOBILE_AIR_COMPRESSOR', text: 'Mobile Air Compressor' },
    { value: 'MOBILE_GAS_COMPRESSOR', text: 'Mobile Gas Compressor' },
    { value: 'MOBILE_GENERATOR', text: 'Mobile Generator' },
    { value: 'MOBILE_PUMP', text: 'Mobile Pump' },
    { value: 'MOBILE_WELDER', text: 'Mobile Welder' },
    { value: 'OFF_HIGHWAY_TRACTOR', text: 'Off-highway Tractor' },
    { value: 'OFF_HIGHWAY_TRUCK', text: 'Off-highway Truck' },
    { value: 'PAVING_OR_SURFACING_EQUIPMENT', text: 'Paving or Surfacing Equipment' },
    { value: 'ROUGH_TERRAIN_FORKLIFT', text: 'Rough Terrain Forklift' },
    { value: 'RUBBER_TIRE_DOZER_OR_LOADER', text: 'Rubber Tire Dozer or Loader' },
    { value: 'SCRAPER_OR_GRADER', text: 'Scraper or Grader' },
    { value: 'SKID_STEER_LOADER', text: 'Skid Steer Loader' },
    { value: 'SWEEPER_OR_SCRUBBER', text: 'Sweeper or Scrubber' },
    { value: 'TRENCHER', text: 'Trencher' },
    { value: 'OTHER_CONSTRUCTION_EQUIPMENT', text: 'Other Construction Equipment' },
    { value: 'OTHER_MATERIAL_HANDLING_EQUIPMENT', text: 'Other Material Handling Equipment' },
]

function isCovered (model) {
    // On-road, non-diesel -> not covered
    if (model.on_road && model.fuel_type === 'NON_DIESEL') {
        return false
    }
    // On-road, diesel -> covered
    else if (model.on_road && model.fuel_type === 'DIESEL') {
        return true
    }
    // Non-road, non-diesel -> not covered
    else if (!model.on_road && model.fuel_type === 'NON_DIESEL') {
        return false
    }
        // Non-road, diesel, 25 and under -> not covered
    // Non-road, diesel, over 25 -> covered
    else if (!model.on_road && model.fuel_type === 'DIESEL') {
        if (model.horsepower_level === '25_OR_LESS') {
            return false
        } else if (model.horsepower_level === 'OVER_25') {
            return true
        }
    }
}

export default {
    onRoadTypeOptions,
    nonRoadTypeOptions,
    getTypeLabel (value) {
        let types = this.fields.type_onroad.options.concat(this.fields.type_nonroad.options)
        let label = types.find(t => t.value === value)
        return label?.text
    },
    fields: {
        on_road: {
            label: 'Equipment Category',
            type: 'radios',
            helpTag: 'equipment-category',
            autocomplete: false,
            options: [
                { value: 1, text: 'On-Road' },
                { value: 0, text: 'Non-Road' },
            ],
            onChange (model, $vform, fieldModel) {
                model.type_onroad = null
                model.retrofit_manufacturer_id = null
                model.retrofit_device_id = null
                model.horsepower_level = null
                fieldModel.fields.retrofit_manufacturer_id.loadOptions(model)
            },
            validations: { required },
        },
        vehicle_id: {
            label: 'Equipment or Vehicle ID/Name',
            type: 'text',
            validations: { required },
            helpTag: 'equipment-vehicle-id',
            getDescription () {return 'ID used by the owner to track the equipment - e.g. license plate number or equipment serial number' },
        },
        type: {
            // display only
            label: 'Equipment Type',
            type: 'radios',
            options: _.concat(onRoadTypeOptions, nonRoadTypeOptions),
        },
        type_onroad: {
            dbFieldName: 'type',
            label: 'On-Road Equipment Type',
            type: 'radios',
            helpTag: 'equipment-onroad',
            showIf (model) {
                const isShown = model.on_road
                if (!isShown) {
                    model.type_onroad = null
                }
                return isShown
            },
            options: onRoadTypeOptions,
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.type_onroad.showIf(this.form) // this = DataForm
                })
            }
        },
        type_nonroad: {
            dbFieldName: 'type',
            label: 'Non-Road Equipment Type',
            type: 'radios',
            showIf (model) {
                const isShown = model.on_road === 0 && (model.fuel_type === 'NON_DIESEL' || (model.fuel_type === 'DIESEL' && model.horsepower_level !== null))
                if (!isShown) {
                    model.type_nonroad = null
                }
                return isShown
            },
            options: nonRoadTypeOptions,
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.type_nonroad.showIf(this.form) // this = DataForm
                })
            }
        },
        fuel_type: {
            label: 'Fuel Type',
            type: 'radios',
            helpTag: 'equipment-fuel-type',
            options: [
                { value: 'DIESEL', text: 'Diesel' },
                { value: 'NON_DIESEL', text: 'Non-Diesel' },
            ],
            showIf (model) {
                const isShown = model.on_road !== null
                if (!isShown) {
                    model.fuel_type = null
                }
                return isShown
            },
            onChange (model) {
                model.horsepower_level = null
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.fuel_type.showIf(this.form) // this = DataForm
                })
            },
        },
        horsepower_level: {
            label: 'Horsepower Level',
            type: 'radios',
            options: [
                { value: 'OVER_25', text: 'More than 25 HP' },
                { value: '25_OR_LESS', text: '25 HP or less' },
            ],
            showIf (model) {
                const isShown = model.on_road === 0 && model.fuel_type === 'DIESEL'
                if (!isShown) {
                    model.horsepower_level = null
                }
                return isShown
            },
            onChange (model) {
                model.type_nonroad = null
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.horsepower_level.showIf(this.form) // this = DataForm
                })
            },
        },
        license: {
            label: 'License',
            type: 'text',
            helpTag: 'equipment-license',
            showIf (model) {
                const isShown = model.on_road
                if (!isShown) {
                    model.license = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.license.showIf(this.form) // this = DataForm
                })
            }
        },
        vin: {
            label: 'VIN',
            type: 'text',
            helpTag: 'equipment-vin',
            showIf (model) {
                const isShown = model.on_road && model.fuel_type === 'DIESEL'
                if (!isShown) {
                    model.vin = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.vin.showIf(this.form) // this = DataForm
                })
            }
        },
        equipment_model_year: {
            label: 'Vehicle Model Year',
            type: 'number',
            minValue: 1995,
            showIf (model) {
                const isShown = model.on_road && model.fuel_type === 'DIESEL'
                if (!isShown) {
                    model.equipment_model_year = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.equipment_model_year.showIf(this.form) // this = DataForm
                }),
            },
        },
        equipment_manufacturer: {
            label: 'Manufacturer/Make',
            type: 'text',
            validations: { required },
        },
        equipment_model: {
            label: 'Model',
            type: 'text',
            getLabel (model) {
                return model.on_road ? 'Vehicle Model' : 'Equipment Model'
            },
            showIf (model) {
                const isShown = !(model.on_road && model.fuel_type === 'NON_DIESEL')
                if (!isShown) {
                    model.equipment_model = null
                }
                return isShown
            },
        },
        equipment_serial_number: {
            label: 'Equipment Serial Number',
            type: 'text',
            showIf (model) {
                const isShown = !model.on_road && isCovered(model)
                if (!isShown) {
                    model.equipment_serial_number = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.equipment_serial_number.showIf(this.form) // this = DataForm
                })
            },
        },
        engine_manufacturer: {
            label: 'Engine Manufacturer',
            type: 'text',
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engine_manufacturer = null
                }
                return isShown
            },
            validations: { required },
        },
        engine_model: {
            label: 'Engine Model',
            type: 'text',
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engine_model = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.engine_model.showIf(this.form) // this = DataForm
                })
            }
        },
        engine_model_year: {
            label: 'Engine Model Year',
            type: 'number',
            minValue: 1995,
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engine_model_year = null
                }
                return isShown
            },
            validations: { required },
        },
        engineFamily: {
            label: 'Engine Family',
            helpTag: 'equipment-engine-family',
            type: 'multiselect',
            multiselectOptions: {
                placeholder: '',
                label: 'autocompleteLabel',
                multiple: false,
                trackBy: 'autocompleteLabel',
                hideSelected: false,
                noMatchMessage: '',
                onSelect: (item, model, $vform, fieldModel, component) => {
                    let message = null
                    if (item === null || _.isArray(item)) {
                        // value cleared
                        model.engine_family_name = null
                        model.engine_manufacturer = null
                        model.engine_model_year = null
                        model.engine_tier = null
                    } else if (item.query) {
                        // no match found
                        model.engine_family_name = item.query
                        model.engine_tier = null
                        message = `No matching Engine Family was found.
                            Please enter the engine manufacturer, model year and HP.`
                    } else {
                        // match found
                        model.engine_family_name = item.autocompleteLabel
                        model.engine_manufacturer = item.manufacturer
                        model.engine_model_year = item.year
                        model.engine_tier = item.tier
                        if (item.tier !== null) {  // NB: OVERRIDE-T0 has Tier 0
                            message = `A matching engine family was found, and the engine manufacturer and model year has been filled in.  
                                Please verify these values before continuing to the next tab.
                                You do not need to enter the engine HP.`
                        } else {
                            message = `A matching engine family was found, and the engine manufacturer and model year has been filled in.
                                However, the exhaust tier could not be determined.
                                Please enter the engine HP, and verify all values before continuing to the next tab.`
                        }
                    }
                    component.$bvModal.msgBoxOk(message)
                }
            },
            async doSearch (query) {
                const items = await Api.getItems(this, 'engine-family', { name: { field: 'name', value: query } })
                if (!items.length) {
                    items.push({ name: '', query: `[No match found] ${query}` })
                }
                return items
            },
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engineFamily = null
                }
                return isShown
            },
            validations: { required: i => !!i && !!(i.autocompleteLabel || i.query) },
            getDescription () {
                return 'Enter engine family name to look up manufacturer, model year, and HP. If no match is found, you must enter these values.'
            },
        },
        engine_family_name: {
            label: 'Engine Family',
            // NB: we need this field on the form so that the value set by engineFamilyName above gets saved!
            // (Validation.extractFormModel only looks at fields on the form)
            type: 'hidden',
            // NB: This hidden field needs showIf for correct equipment validity
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engine_family_name = null
                }
                return isShown
            },
            validations: { required }
        },
        engine_tier: {
            type: 'hidden',
        },
        engine_serial_number: {
            label: 'Engine Serial Number',
            type: 'text',
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engine_serial_number = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.engine_serial_number.showIf(this.form) // this = DataForm
                })
            }
        },
        engine_hp: {
            label: 'Engine HP',
            type: 'number',
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.engine_hp = null
                }
                return isShown
            },
            disableIf (model) {
                return model.engine_tier !== null
            },
            getDescription (model) {
                if (model.engine_tier !== null) {
                    return 'Not required if Engine Family match is found'
                } else {
                    return ''
                }
            },
            validations: {
                required: requiredIf(function (model) {
                    return model.engine_tier === null
                })
            }
        },
        exhaust_retrofit_type: {
            label: 'Aftermarket Exhaust Retrofit Type',
            type: 'radios',
            helpTag: 'equipment-exhaust-retrofit-type',
            getDescription () {return 'Explicit help here' },
            options: [
                { value: 'NONE', text: 'No Retrofits' },
                { value: 'DPF', text: 'Diesel Particulate Filter (DPF)' },
                { value: 'DOC', text: 'Diesel Oxidation Catalyst (DOC)' },
            ],
            showIf (model) {
                const isShown = isCovered(model)
                if (!isShown) {
                    model.exhaust_retrofit_type = null
                }
                return isShown
            },
            onChange (model) {
                if (model.exhaust_retrofit_type === 'NONE') {
                    model.retrofit_manufacturer_id = null
                    model.retrofit_device_id = null
                }
            },
            validations: { required }
        },
        retrofit_manufacturer_id: {
            label: 'Retrofit manufacturer',
            type: 'select',
            options: [],
            async loadOptions (model) {
                RetrofitManufacturer.loadRetrofitManufacturerOptions(this, model)
            },
            showIf (model) {
                const isShown = isCovered(model) && (model.exhaust_retrofit_type === 'DPF' || model.exhaust_retrofit_type === 'DOC')
                if (!isShown) {
                    model.retrofit_manufacturer_id = null
                }
                return isShown
            },
            onChange (model, $vform, fieldModel) {
                fieldModel.fields.retrofit_device_id.loadOptions(model)
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.retrofit_manufacturer_id.showIf(this.form) // this = DataForm
                })
            },
        },
        retrofit_device_id: {
            label: 'Retrofit model',
            type: 'select',
            options: [],
            async loadOptions (model) {
                RetrofitDevice.loadRetrofitDeviceOptions(this, model)
            },
            showIf (model) {
                const isShown = isCovered(model) && (model.exhaust_retrofit_type === 'DPF' || model.exhaust_retrofit_type === 'DOC')
                if (!isShown) {
                    model.retrofit_device_id = null
                }
                return isShown
            },
            // TODO: figure out why this gives the following error: (this may no longer be an issue)
            // [Vue warn]: You may have an infinite update loop in a component render function.

            disableIf (model) {
                return !model.retrofit_manufacturer_id
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.retrofit_device_id.showIf(this.form) // this = DataForm
                })
            },
        },
        retrofit_serial_number: {
            label: 'Retrofit serial number',
            type: 'text',
            showIf (model) {
                const isShown = isCovered(model) && (model.exhaust_retrofit_type === 'DPF' || model.exhaust_retrofit_type === 'DOC')
                if (!isShown) {
                    model.retrofit_serial_number = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.retrofit_serial_number.showIf(this.form) // this = DataForm
                })
            }
        },
        retrofit_installation_year: {
            label: 'Year of retrofit installation',
            type: 'text',
            showIf (model) {
                const isShown = isCovered(model) && (model.exhaust_retrofit_type === 'DPF' || model.exhaust_retrofit_type === 'DOC')
                if (!isShown) {
                    model.retrofit_installation_year = null
                }
                return isShown
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.retrofit_installation_year.showIf(this.form) // this = DataForm
                })
            }
        },
        updated: { // read-only
            label: 'Last Updated',
            type: 'date'
        },
        attested: { // read-only
            label: 'Attested',
            type: 'date'
        },
        registrationNumber: { // read-only
            label: 'Registration Number',
            type: 'number'
        },
        'retrofit_manufacturer.name': { // read-only
            label: 'Retrofit Manufacturer',
            type: 'string'
        },
        'retrofit_device.name': { // read-only
            label: 'Retrofit Device',
            type: 'string'
        },
        'company.name': { // read-only
            label: 'Company',
            type: 'string'
        },
        notes: {
            label: 'Notes',
            type: 'textarea',
        },
    }
}
